import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMatch, getPredictionsForMatch, requestInsights, requestPreview, requestRecap, submitPrediction, type PredPick } from '../api';
import CommentsSection from '../components/CommentsSection';
import MatchChat from '../components/MatchChat';
import MatchPropsPanel from '../components/MatchPropsPanel';
import AdminResolveMatch from '../components/AdminResolveMatch';
import EventTimeline from '../components/EventTimeline';
import LineupsPanel from '../components/LineupsPanel';
import WinProbChart from '../components/WinProbChart';
import PitchFormation from '../components/PitchFormation';
import { toast } from '../toast';
import { haptic } from '../haptic';
import { useAuth } from '../auth';
import PageHeader from '../components/PageHeader';
import { fmtOdds, impliedProbs } from '../odds';
import { rigaDate, rigaTime, stageLabel } from '../format';

export default function MatchDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['match', id],
    queryFn: () => getMatch(id),
    refetchInterval: 30_000,
  });
  const { data: predsData } = useQuery({
    queryKey: ['preds', id],
    queryFn: () => getPredictionsForMatch(id),
  });

  const preview = useMutation({
    mutationFn: () => requestPreview(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['match', id] }),
  });
  const recap = useMutation({
    mutationFn: () => requestRecap(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['match', id] }),
  });
  const insights = useMutation({
    mutationFn: () => requestInsights(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['match', id] }),
  });

  if (isLoading) return <PageHeader title="Match" />;
  if (!data) return null;

  const { match: m, summary, preview: previewText, recap: recapText, aiPick, insights: insightsList } = data;
  const probs = impliedProbs(m);
  const isPre = m.status === 'pre';
  const isLive = m.status === 'in';
  const isDone = m.status === 'post';

  const myPrediction = predsData?.items.find((p) => p.user === user);
  const allPredictions = predsData?.items ?? [];

  return (
    <div className={isLive ? 'bg-pitch-live -m-4 lg:m-0 lg:rounded-2xl p-4 lg:p-0' : ''}>
      <PageHeader
        title={`${m.home_flag ?? '🏳️'} ${m.home_name} vs ${m.away_flag ?? '🏳️'} ${m.away_name}`}
        subtitle={
          <span>
            <button onClick={() => nav(-1)} className="text-pitch-400 hover:underline">← Back</button>
            <span className="ml-2">{rigaDate(m.utc_kickoff)} · {rigaTime(m.utc_kickoff)} · {stageLabel(m.stage)}</span>
            {summary?.venue.name && <span className="ml-2 text-dim">· {summary.venue.name}{summary.venue.city ? `, ${summary.venue.city}` : ''}</span>}
          </span>
        }
      />

      <div className="px-4 lg:px-0 space-y-4">

        {/* Score / status hero */}
        <section className="card p-4 flex items-center justify-around text-center gap-3">
          <Side flag={m.home_flag ?? null} name={m.home_name} owner={m.home_owner ?? null} score={m.home_score} winner={m.winner === 'home'} dim={isDone && m.winner !== 'home'} />
          <div>
            {isLive && <div className="text-xs text-pitch-400 font-mono uppercase tracking-wider">{m.status_detail ?? 'LIVE'}</div>}
            {isDone && (
              m.winner === null && m.stage !== 'group'
                ? <div className="text-xs text-amber-400 font-mono uppercase tracking-wider" title="Knockout match — extra time / penalties pending">Awaiting ET/PK</div>
                : <div className="text-xs text-dim">{m.ended_phase === 'ot' ? 'AET' : m.ended_phase === 'pk' ? 'Pens.' : 'FT'}</div>
            )}
            {isPre && <div className="text-xs text-dim">{rigaTime(m.utc_kickoff)}</div>}
            <div className="text-xs text-dim font-mono mt-1">vs</div>
          </div>
          <Side flag={m.away_flag ?? null} name={m.away_name} owner={m.away_owner ?? null} score={m.away_score} winner={m.winner === 'away'} dim={isDone && m.winner !== 'away'} />
        </section>

        {/* Admin: resolve stuck KO match (only renders when admin key + match is stuck) */}
        {isDone && m.winner === null && m.stage !== 'group' && (
          <AdminResolveMatch
            matchId={id} homeName={m.home_name} awayName={m.away_name}
            homeScore={m.home_score} awayScore={m.away_score}
          />
        )}

        {/* AI preview or recap */}
        <section className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs uppercase tracking-widest text-dim">
              {isDone ? 'AI recap' : 'AI preview'}
            </h2>
            {isDone ? (
              <button
                onClick={() => recap.mutate()}
                disabled={recap.isPending}
                className="text-[10px] text-pitch-400 hover:underline disabled:opacity-50"
              >{recapText ? 'Refresh' : 'Generate'}</button>
            ) : (
              <button
                onClick={() => preview.mutate()}
                disabled={preview.isPending}
                className="text-[10px] text-pitch-400 hover:underline disabled:opacity-50"
              >{previewText ? 'Refresh' : 'Generate'}</button>
            )}
          </div>
          {(isDone ? recapText : previewText) ? (
            <p className="text-sm leading-snug">{isDone ? recapText : previewText}</p>
          ) : (
            <p className="text-xs text-dim">
              {(isDone ? recap.isPending : preview.isPending) ? 'Generating…' : 'Nothing yet. Tap Generate.'}
            </p>
          )}
        </section>

        {/* AI insights */}
        <section className="card p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs uppercase tracking-widest text-dim">AI insights</h2>
            <button
              onClick={() => insights.mutate()}
              disabled={insights.isPending}
              className="text-[10px] text-cyan-400 hover:underline disabled:opacity-50"
            >{insightsList?.length ? 'Refresh' : 'Generate'}</button>
          </div>
          {insightsList?.length ? (
            <ul className="space-y-1.5 text-sm leading-snug">
              {insightsList.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-cyan-400 shrink-0">▸</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-dim">
              {insights.isPending ? 'Generating…' : 'Tap Generate for 4 analytical notes on this match.'}
            </p>
          )}
        </section>

        {/* AI pick */}
        {aiPick && (
          <section className="card p-4 ring-1 ring-cyan-500/20">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xs uppercase tracking-widest text-dim">AI pick</h2>
              <span className="text-[10px] text-dim">Llama 3.3 70B</span>
            </div>
            <div className="text-sm">
              <span className="font-semibold text-cyan-400">
                {aiPick.pick === '1' ? `${m.home_name} wins` : aiPick.pick === '2' ? `${m.away_name} wins` : 'Draw'}
                {' '}
                <span className="stat">{aiPick.home_score}-{aiPick.away_score}</span>
              </span>
            </div>
            {aiPick.reasoning && (
              <p className="text-xs text-dim mt-1.5 leading-snug">"{aiPick.reasoning}"</p>
            )}
          </section>
        )}

        {/* Match chatbot — sits in the AI cluster so it's discoverable */}
        <MatchChat matchId={id} homeName={m.home_name} awayName={m.away_name} />

        {/* Full props panel */}
        {(m.ml_home || m.ml_draw || m.ml_away) && (
          <MatchPropsPanel matchId={id} homeName={m.home_name} awayName={m.away_name} />
        )}

        {/* 1 X 2 */}
        {(m.ml_home || m.ml_draw || m.ml_away) && (
          <section className="card p-4">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Bookmaker 1 X 2</h2>
            <div className="grid grid-cols-3 gap-2">
              {(['1', 'X', '2'] as const).map((k) => {
                const coef = k === '1' ? m.ml_home : k === 'X' ? m.ml_draw : m.ml_away;
                const p = probs ? (k === '1' ? probs.home : k === 'X' ? probs.draw : probs.away) : null;
                return (
                  <div key={k} className="rounded-lg bg-ink-800/80 p-2 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-dim">{k}</div>
                    <div className="stat text-lg">{fmtOdds(coef)}</div>
                    {p != null && <div className="text-[10px] text-pitch-400">{Math.round(p * 100)}%</div>}
                  </div>
                );
              })}
            </div>
            {m.over_under != null && <div className="mt-2 text-[10px] text-dim text-right">O/U {m.over_under} · {m.odds_provider}</div>}
          </section>
        )}

        {/* Predictions */}
        {user && (
          <section className="card p-4">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Your prediction</h2>
            {isPre ? (
              <PredictionForm matchId={id} current={myPrediction} disabled={!isPre} />
            ) : (
              <p className="text-xs text-dim">Predictions are locked once the match starts.</p>
            )}
          </section>
        )}

        {allPredictions.length > 0 && (
          <section className="card p-4">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">League picks</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {allPredictions.map((p) => (
                <div key={p.user} className="rounded-lg bg-ink-800 px-2.5 py-1.5">
                  <div className="text-[10px] text-dim">{p.user}</div>
                  <div className="text-sm font-semibold">{p.pick}{p.home_score != null && p.away_score != null && ` · ${p.home_score}-${p.away_score}`}</div>
                  {p.points != null && (
                    <div className={`text-[10px] mt-0.5 ${p.points > 0 ? 'text-pitch-400' : 'text-dim'}`}>{p.points} pts</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Win probability (pre-match bar → live chart at KO) */}
        <WinProbChart
          matchId={id}
          homeName={m.home_name}
          awayName={m.away_name}
          preMatch={probs}
          status={m.status}
        />

        {/* Match events (icons, team-attributed) */}
        {summary && (
          <EventTimeline events={summary.events} homeName={m.home_name} awayName={m.away_name} />
        )}

        {/* Pitch formation visual */}
        {summary && summary.rosters.some((r) => r.starters.length > 0) && (
          <section className="card p-4">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Formations</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {summary.rosters.map((r) => (
                <PitchFormation key={r.teamCode || r.teamName} roster={r} />
              ))}
            </div>
          </section>
        )}

        {/* Lineups + per-player stats + bench */}
        {summary && <LineupsPanel rosters={summary.rosters} />}

        {/* Live commentary */}
        {summary && summary.commentary.length > 0 && (
          <section className="card p-4">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Live commentary</h2>
            <ol className="space-y-2 max-h-96 overflow-y-auto">
              {summary.commentary.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-xs leading-snug">
                  <span className="stat text-dim w-10 shrink-0 text-right">{c.minute || '—'}</span>
                  <span className="flex-1">{c.text}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Comments + reactions */}
        <CommentsSection matchId={id} />

        {/* Head-to-head + broadcasts */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {summary?.headToHead && summary.headToHead.games > 0 && (
            <div className="card p-4">
              <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Head-to-head</h2>
              <div className="flex justify-between items-baseline">
                <div className="text-center">
                  <div className="stat text-xl">{summary.headToHead.homeWins}</div>
                  <div className="text-[10px] text-dim">{m.home_name}</div>
                </div>
                <div className="text-center">
                  <div className="stat text-xl">{summary.headToHead.draws}</div>
                  <div className="text-[10px] text-dim">Draws</div>
                </div>
                <div className="text-center">
                  <div className="stat text-xl">{summary.headToHead.awayWins}</div>
                  <div className="text-[10px] text-dim">{m.away_name}</div>
                </div>
              </div>
              <div className="text-[10px] text-dim text-center mt-2">{summary.headToHead.games} recorded</div>
            </div>
          )}
          <div className="card p-4">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Watch in Latvia</h2>
            <div className="flex flex-wrap gap-1.5">
              <a href="https://go3.lv/sports" target="_blank" rel="noopener noreferrer" className="pill bg-ink-800 text-chalk hover:bg-ink-700">Go3 · Sports</a>
              <a href="https://www.tv3.lv/sports" target="_blank" rel="noopener noreferrer" className="pill bg-ink-800 text-chalk hover:bg-ink-700">TV3 Sport</a>
              <a href="https://replay.lsm.lv/lv/" target="_blank" rel="noopener noreferrer" className="pill bg-ink-800 text-chalk hover:bg-ink-700">LSM Replay (LTV)</a>
              <a href="https://www.sportacentrs.com/futbols" target="_blank" rel="noopener noreferrer" className="pill bg-ink-800 text-chalk hover:bg-ink-700">Sportacentrs</a>
            </div>
            <p className="text-[10px] text-dim mt-2">Confirmed Latvian rights-holder for this specific match may vary — links go to each platform's football section.</p>
          </div>
        </section>
      </div>
    </div>
  );
}

function Side({ flag, name, owner, score, winner, dim }: { flag: string | null; name: string; owner: string | null | undefined; score: number | null; winner: boolean; dim: boolean }) {
  return (
    <div className={`flex-1 ${dim ? 'opacity-60' : ''}`}>
      <div className="text-3xl">{flag ?? '🏳️'}</div>
      <div className={`text-sm font-semibold ${winner ? 'text-chalk' : ''} mt-1`}>{name}</div>
      {owner && <div className="text-[10px] text-dim">{owner}</div>}
      {score !== null && <div className="stat text-3xl mt-1">{score}</div>}
    </div>
  );
}

function PredictionForm({ matchId, current, disabled }: { matchId: string; current: { pick: PredPick; home_score: number | null; away_score: number | null } | undefined; disabled: boolean }) {
  const qc = useQueryClient();
  const [pick, setPick] = useState<PredPick | null>(current?.pick ?? null);
  const [homeScore, setHomeScore] = useState<string>(current?.home_score?.toString() ?? '');
  const [awayScore, setAwayScore] = useState<string>(current?.away_score?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: submitPrediction,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['preds', matchId] }); setError(null); toast.success('Prediction saved.'); haptic.success(); },
    onError: (e: Error) => { setError(e.message); toast.error(e.message); haptic.error(); },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!pick) { setError('Pick 1, X, or 2.'); return; }
    mut.mutate({
      match_id: matchId, pick,
      home_score: homeScore !== '' ? Number(homeScore) : null,
      away_score: awayScore !== '' ? Number(awayScore) : null,
    });
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {(['1', 'X', '2'] as PredPick[]).map((k) => (
          <button
            key={k} type="button" disabled={disabled}
            onClick={() => setPick(k)}
            className={`py-2 rounded-lg text-sm font-semibold ${
              pick === k ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'
            }`}
          >{k}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <input type="number" min={0} max={20} value={homeScore} onChange={(e) => setHomeScore(e.target.value)}
          placeholder="Home" disabled={disabled}
          className="flex-1 bg-ink-800 border border-ink-700 rounded-lg px-2 py-1.5 text-center font-mono" />
        <span className="text-dim">–</span>
        <input type="number" min={0} max={20} value={awayScore} onChange={(e) => setAwayScore(e.target.value)}
          placeholder="Away" disabled={disabled}
          className="flex-1 bg-ink-800 border border-ink-700 rounded-lg px-2 py-1.5 text-center font-mono" />
      </div>
      <div className="text-[10px] text-dim">3 pts for correct outcome, +1 for exact score.</div>
      {error && <div className="text-xs text-red-400">{error}</div>}
      <button
        type="submit" disabled={disabled || mut.isPending}
        className="w-full h-10 rounded-lg bg-pitch-500 text-ink-950 font-semibold text-sm hover:bg-pitch-400 disabled:opacity-50"
      >{current ? 'Update prediction' : 'Submit prediction'}</button>
    </form>
  );
}
